import { inject, Injectable } from '@angular/core';
import {
  createHttpResourceLocation,
  Dictionary,
  HttpDataAccess,
  HttpResourceLocation,
  RequestEvent,
} from '@softline/core';
import { Observable } from 'rxjs';
import {
  AI_TABLE_EXTRACTOR_API_JOB,
  AI_TABLE_EXTRACTOR_API_JOB_APPLY_TEMPLATE,
  AI_TABLE_EXTRACTOR_API_JOB_DETECT_APPLY,
  AI_TABLE_EXTRACTOR_API_JOB_PAGE,
  AI_TABLE_EXTRACTOR_API_JOB_PAGES,
  AI_TABLE_EXTRACTOR_API_JOB_TEMPLATE_STATUS,
  AI_TABLE_EXTRACTOR_API_JOB_WORDS,
  AI_TABLE_EXTRACTOR_API_JOBS,
} from '../ai-table-extractor.api';
import {
  DEFAULT_AI_TABLE_EXTRACTOR_CONFIG,
  SOFTLINE_CONFIG_AI_TABLE_EXTRACTOR,
} from '../ai-table-extractor.shared';
import {
  ExtractionJob,
  JobCreatedResponse,
  JobPagesResponse,
  JobWordsResponse,
} from '../types/job';
import {
  ApplyTemplateResponse,
  DetectApplyResponse,
  TemplateStatusResponse,
} from '../types/template';

/**
 * Data access for the belegleser job endpoints. Deliberately built on
 * HttpDataAccess (raw URLs), NOT ApiDataAccess — the extraction service is not
 * the Softline connection, and the base URL comes from the module config.
 */
@Injectable()
export class JobRepository {
  private readonly dataAccess = inject(HttpDataAccess);
  private readonly config = inject(SOFTLINE_CONFIG_AI_TABLE_EXTRACTOR);

  private get baseUrl(): string {
    return this.config.apiBaseUrl ?? DEFAULT_AI_TABLE_EXTRACTOR_CONFIG.apiBaseUrl;
  }

  /** Multipart upload with progress events; the response carries the job id. */
  upload(file: File): Observable<RequestEvent<JobCreatedResponse>> {
    const own = this.config.ownCompany ?? {};
    const payload: Dictionary<unknown> = { file };
    if (own.name) payload['own_name'] = own.name;
    if (own.address) payload['own_address'] = own.address;
    // Sent as ONE comma-separated form field — the service splits on commas.
    if (own.uids?.length) payload['own_uids'] = own.uids.join(',');
    return this.dataAccess.upload(this.location(AI_TABLE_EXTRACTOR_API_JOBS), payload);
  }

  get(jobId: string): Promise<ExtractionJob> {
    return this.dataAccess.get(this.location(AI_TABLE_EXTRACTOR_API_JOB, { jobId }));
  }

  delete(jobId: string): Promise<void> {
    return this.dataAccess.delete(this.location(AI_TABLE_EXTRACTOR_API_JOB, { jobId }));
  }

  pages(jobId: string): Promise<JobPagesResponse> {
    return this.dataAccess.get(
      this.location(AI_TABLE_EXTRACTOR_API_JOB_PAGES, { jobId })
    );
  }

  words(jobId: string): Promise<JobWordsResponse> {
    return this.dataAccess.get(
      this.location(AI_TABLE_EXTRACTOR_API_JOB_WORDS, { jobId })
    );
  }

  /**
   * Recognizes the supplier company and picks its best template by recall —
   * the service does the choosing, the client only applies the winner.
   */
  detectApply(jobId: string): Promise<DetectApplyResponse> {
    return this.dataAccess.create(
      this.location(AI_TABLE_EXTRACTOR_API_JOB_DETECT_APPLY, { jobId }),
      {}
    );
  }

  /**
   * Re-reads the job's cached words with the template and answers a full
   * extraction result (`extraction_method: 'template'`). 404 = template gone.
   * `supplier` is the TEMPLATE KEY, and it goes out as a query parameter.
   */
  applyTemplate(jobId: string, supplier: string): Promise<ApplyTemplateResponse> {
    return this.dataAccess.create(
      this.location(AI_TABLE_EXTRACTOR_API_JOB_APPLY_TEMPLATE, { jobId }, { supplier }),
      {}
    );
  }

  templateStatus(jobId: string): Promise<TemplateStatusResponse> {
    return this.dataAccess.get(
      this.location(AI_TABLE_EXTRACTOR_API_JOB_TEMPLATE_STATUS, { jobId })
    );
  }

  /**
   * URL of the rotation-corrected page PNG, for direct <img> use. The rotation
   * doubles as cache-buster: after completion the same URL must not serve the
   * stale PNG rendered while the job was still pending.
   */
  pageImageUrl(jobId: string, page: number, rotation = 0): string {
    const location = this.location(AI_TABLE_EXTRACTOR_API_JOB_PAGE, { jobId, page });
    return `${location.url}?v=${rotation}`;
  }

  private location(
    path: string,
    pathParams?: Dictionary<unknown>,
    queryParams?: Dictionary<unknown>
  ): HttpResourceLocation {
    return createHttpResourceLocation({
      url: this.baseUrl + path,
      pathParams,
      queryParams,
    });
  }
}
